import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkle, UserCircle, Checks, CaretDown } from '@phosphor-icons/react';
interface ChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}
// Format timestamp into a friendly relative string (e.g. "Just now", "4 mins ago")
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  if (diffSec < 30) return 'Just now';
  if (diffMin < 1) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'min' : 'mins'} ago`;
  if (diffHr < 24) return `${diffHr} ${diffHr === 1 ? 'hr' : 'hrs'} ago`;
  if (diffDay < 7) return `${diffDay} ${diffDay === 1 ? 'day' : 'days'} ago`;
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric'
  });
}
function Avatar({
  role
}: {
  role: 'user' | 'assistant';
}) {
  const isUser = role === 'user';
  return <div className="relative flex-shrink-0" data-id="element-208">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isUser ? 'bg-white/10 border border-white/15' : 'bg-gradient-to-br from-blue-500/35 to-blue-700/20 border border-blue-400/40'}`} data-id="element-209">
        {isUser ? <UserCircle className="w-6 h-6 text-white/70" weight="fill" aria-hidden="true" data-id="element-210" /> : <Sparkle className="w-4 h-4 text-blue-300" weight="fill" aria-hidden="true" data-id="element-211" />}
      </div>
      {/* Online status dot */}
      <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-blue-400 border-2 border-black" aria-hidden="true" data-id="element-212" />
    </div>;
}
export function ChatMessage({
  role,
  content,
  timestamp,
  isStreaming = false
}: ChatMessageProps) {
  const isUser = role === 'user';
  const [isExpanded, setIsExpanded] = useState(false);
  // Check if user message is long (more than 100 characters)
  const isLongMessage = isUser && content.length > 100;
  const shouldCollapse = isLongMessage && !isExpanded;
  const name = isUser ? 'You' : 'TAIO';
  const relativeTime = formatRelativeTime(timestamp);
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} gap-1.5`} data-id="element-213">
      {/* Header row: avatar + name + timestamp (mirrored for user) */}
      <div className={`flex items-center gap-2.5 ${isUser ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-214">
        <Avatar role={role} data-id="element-215" />
        <div className={`flex items-center gap-2 text-xs ${isUser ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-216">
          <span className="font-medium text-white/90" data-id="element-217">{name}</span>
          <span className="text-white/30" aria-hidden="true" data-id="element-218">
            |
          </span>
          <span className="text-white/50" data-id="element-219">
            {isStreaming ? 'Typing...' : relativeTime}
          </span>
        </div>
      </div>
      {/* Bubble row: bubble + read receipt (mirrored for user) */}
      <div className={`flex items-end gap-2 max-w-[88%] ${isUser ? 'flex-row pl-11' : 'flex-row-reverse pr-11'}`} data-id="element-220">
        {/* Read receipt — placed on the inside edge of the bubble */}
        <Checks className="w-3.5 h-3.5 text-blue-400/70 mb-2 flex-shrink-0" weight="bold" aria-label={isUser ? 'Sent' : 'Delivered'} data-id="element-221" />
        <div className={`px-4 py-2.5 rounded-3xl ${isUser ? 'bg-white text-slate-900 rounded-br-md' : 'bg-white/[0.08] text-white border border-white/10 rounded-bl-md'} ${isLongMessage ? 'cursor-pointer transition-colors' : ''}`} onClick={() => isLongMessage && setIsExpanded(!isExpanded)} data-id="element-222">
          <p className={`text-sm leading-relaxed whitespace-pre-wrap ${shouldCollapse ? 'line-clamp-1' : ''}`} data-id="element-223">
            {content}
            {isStreaming && <motion.span animate={{
            opacity: [0, 1, 0]
          }} transition={{
            duration: 1,
            repeat: Infinity
          }} className="inline-block w-1 h-4 ml-1 bg-current align-middle" data-id="element-224" />}
          </p>
          {isLongMessage && <button className="flex items-center gap-1 mt-1 text-xs opacity-60 hover:opacity-100 transition-opacity" aria-label={isExpanded ? 'Collapse message' : 'Expand message'} data-id="element-225">
              <CaretDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} weight="bold" data-id="element-226" />
            </button>}
        </div>
      </div>
    </motion.div>;
}